import { Link } from "react-router-dom";
import { Compass, Shield, BookOpen, Sparkles } from "lucide-react";
import { PageHero, Reveal, SectionHeading } from "@/components/common/Reveal";
import { InstagramBlock } from "@/components/common/InstagramBlock";
import { ChampSection } from "@/components/common/ChampSection";

const VALUES = [
  { icon: Compass, title: "Strategy", text: "Every decision is intentional. Strategy over everything." },
  { icon: Shield, title: "Integrity", text: "Honest guidance and confidential handling, every time." },
  { icon: BookOpen, title: "Education", text: "We teach the why, so our clients can own their future." },
  { icon: Sparkles, title: "Service", text: "Simply good people providing exceptional service." },
];

const BRANDS = [
  { src: "/images/logo-b2r-tax.jpg", alt: "Baggs 2 Riches Tax Company logo", tag: "Your Refund, Your Riches" },
  { src: "/images/logo-b2r-hex.jpg", alt: "Baggs 2 Riches Tax Company hexagon logo", tag: "Let's Secure the Bag Together" },
];

const Brands = () => (
  <section className="bg-black py-24 md:py-32 border-t border-white/10" data-testid="brands-section">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <SectionHeading eyebrow="The B2R Family" title={<>Baggs 2 Riches <span className="italic gold-text">Tax Company</span></>} />
      <div className="mt-14 grid md:grid-cols-2 gap-6">
        {BRANDS.map((b, i) => (
          <Reveal key={b.src} delay={i * 0.1}>
            <div className="lux-card lift p-4" data-testid={`brand-card-${i}`}>
              <div className="bg-white rounded-xl h-64 flex items-center justify-center p-6"><img src={b.src} alt={b.alt} className="max-h-full w-auto object-contain" /></div>
              <p className="mt-5 mb-2 text-center font-display italic text-2xl text-[#D4AF37]">“{b.tag}”</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const BIO = [
  "I am Daja Culver, a proud single mother of six, a God-fearing woman, and an entrepreneur who believes that our circumstances do not define our destiny.",
  "My journey hasn’t always been easy, but through every challenge, God continued to guide my steps. With the wisdom, encouragement, and guidance of an incredible mentor, I discovered more than a career in the financial and tax industry ❤️ I discovered my calling.",
  "That calling inspired me to create Baggs 2 Riches LLC, a company built on faith, integrity, education, and opportunity.",
  "As a mother, I understand the importance of financial stability and building a legacy. My mission is to help individuals and families reach their financial goals, empower aspiring entrepreneurs, and remind others that their dreams are still possible, regardless of where they started.",
  "I believe true success isn’t measured by what we gain, but by the lives we touch and the opportunities we create for others.",
  "Baggs 2 Riches is more than a business to me. It’s my purpose, my passion, and my way of serving God’s people.",
];

const Founder = () => (
  <section className="bg-black py-24 md:py-32" data-testid="founder-section">
    <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-12 md:gap-20 items-start">
      <Reveal className="md:col-span-5 md:sticky md:top-28">
        <div className="relative">
          <div className="absolute -inset-4 border border-[#D4AF37]/30 rounded-[2rem] translate-x-3 translate-y-3 md:translate-x-5 md:translate-y-5 hidden sm:block" />
          <img src="/images/founder-daja.jpg" srcSet="/images/founder-daja-m.jpg 560w, /images/founder-daja.jpg 704w" sizes="(max-width: 768px) 100vw, 40vw" alt="Daja Culver, Founder of Baggs 2 Riches LLC" data-testid="founder-portrait"
            className="relative w-full aspect-[4/5] object-cover object-top rounded-[2rem] border border-[#D4AF37]/40 purple-glow" />
        </div>
      </Reveal>
      <div className="md:col-span-7">
        <SectionHeading eyebrow="Meet the Founder" title={<>Daja <span className="italic gold-text">Culver</span></>} />
        <Reveal delay={0.15}>
          <p className="mt-3 text-sm tracking-[0.2em] uppercase text-white/50">Founder &amp; CEO · Baggs 2 Riches LLC</p>
          <blockquote className="mt-8 border-l-2 border-[#D4AF37] pl-6 font-display text-2xl md:text-3xl italic text-white/90" data-testid="founder-quote">
            “My journey is rooted in faith, fueled by purpose, and dedicated to serving others.”
          </blockquote>
          <div className="mt-8 space-y-5 text-white/75 font-light text-base md:text-lg leading-relaxed" data-testid="founder-bio">
            {BIO.map((p) => <p key={p.slice(0, 24)}>{p}</p>)}
          </div>
          <div className="mt-10" data-testid="founder-signature">
            <p className="text-white/60 italic">With faith, love, and gratitude,</p>
            <p className="mt-3 font-display text-4xl gold-text italic">Daja Culver</p>
            <p className="mt-2 text-xs tracking-[0.2em] uppercase text-white/50">Founder &amp; CEO | Baggs 2 Riches LLC</p>
            <p className="mt-4 font-display text-xl italic text-[#D4AF37]">“Where Strategy Meets Opportunity.”</p>
          </div>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link to="/book" className="btn-gold" data-testid="founder-book-button">Book with B2R</Link>
            <Link to="/careers" className="btn-glass" data-testid="founder-careers-button">Join the Team</Link>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

export default function About() {
  return (
    <div data-testid="about-page">
      <PageHero testid="about-hero" eyebrow="About B2R" title={<>Where strategy meets <span className="italic gold-text">opportunity.</span></>}
        sub="Baggs 2 Riches LLC is a financial services and tax consulting company, certified to file taxes in all 50 states, dedicated to helping people build lasting financial freedom." />
      <Founder />
      <Brands />
      <ChampSection />
      <section className="bg-[#0A0A0A] py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <SectionHeading eyebrow="What We Stand For" title="Our values" />
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.08}>
                <div className="lux-card lift p-8 h-full" data-testid={`value-card-${v.title.toLowerCase()}`}>
                  <v.icon className="w-8 h-8 text-[#D4AF37]" strokeWidth={1.3} />
                  <h3 className="mt-6 text-3xl font-medium">{v.title}</h3>
                  <p className="mt-3 text-sm text-white/60 leading-relaxed">{v.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
      <InstagramBlock />
    </div>
  );
}
